import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { Link, Outlet, useMatchRoute, useNavigate, useParams } from "@tanstack/react-router";
import type { Permission } from "@zeeta/shared";
import {
  BarChart3,
  Bike,
  Boxes,
  Code2,
  CreditCard,
  FileSignature,
  FileText,
  Gift,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  Menu,
  MonitorSmartphone,
  Package,
  Plus,
  Receipt,
  Settings,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Store,
  Tags,
  Truck,
  Users,
  UsersRound,
  Wallet,
  WalletCards,
} from "lucide-react";
import { useState } from "react";
import { LanguageToggle } from "@/components/language-toggle";
import { Logo } from "@/components/logo";
import { SiteFooter } from "@/components/site-footer";
import { ThemeToggle } from "@/components/theme-toggle";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Select } from "@/components/ui/field";
import { AskZeetaButton } from "@/features/ai/ask-zeeta";
import { ForcePasswordChange } from "@/features/auth/force-password-change";
import { meQuery } from "@/features/auth/queries";
import {
  allowanceQuery,
  type BusinessAllowance,
  BusinessLimitNotice,
} from "@/features/onboarding/business-allowance";
import { outboxFor } from "@/features/pos/offline";
import { type MessageKey, useI18n } from "@/i18n";
import { api } from "@/lib/api";
import { TenantProvider, useTenant } from "./context";
import { PlatformBanners } from "./platform-banners";
import { overviewQuery } from "./queries";
import { useLiveUpdates } from "./use-live-updates";

type NavTo =
  | "/t/$tenantId"
  | "/t/$tenantId/pos"
  | "/t/$tenantId/sales"
  | "/t/$tenantId/orders"
  | "/t/$tenantId/storefront"
  | "/t/$tenantId/couriers"
  | "/t/$tenantId/developers"
  | "/t/$tenantId/products"
  | "/t/$tenantId/stock"
  | "/t/$tenantId/purchases"
  | "/t/$tenantId/customers"
  | "/t/$tenantId/suppliers"
  | "/t/$tenantId/wholesale"
  | "/t/$tenantId/loyalty"
  | "/t/$tenantId/quotations"
  | "/t/$tenantId/accounts"
  | "/t/$tenantId/expenses"
  | "/t/$tenantId/reports"
  | "/t/$tenantId/vat"
  | "/t/$tenantId/settings"
  | "/t/$tenantId/team"
  | "/t/$tenantId/billing"
  | "/t/$tenantId/sessions";

interface NavItem {
  to: NavTo;
  label: MessageKey;
  icon: typeof LayoutDashboard;
  /** Shown when the member has any of these permissions. */
  any?: Permission[];
  mobile?: boolean;
}

const NAV: NavItem[] = [
  { to: "/t/$tenantId", label: "nav.dashboard", icon: LayoutDashboard, mobile: true },
  {
    to: "/t/$tenantId/pos",
    label: "nav.pos",
    icon: ShoppingCart,
    any: ["sale.create"],
    mobile: true,
  },
  {
    to: "/t/$tenantId/sales",
    label: "nav.sales",
    icon: Receipt,
    any: ["sale.create", "report.sales.view"],
    mobile: true,
  },
  {
    to: "/t/$tenantId/quotations",
    label: "nav.quotations",
    icon: FileSignature,
    any: ["sale.create"],
  },
  { to: "/t/$tenantId/orders", label: "nav.orders", icon: ShoppingBag, any: ["order.manage"] },
  {
    to: "/t/$tenantId/storefront",
    label: "nav.storefront",
    icon: Store,
    any: ["storefront.manage"],
  },
  { to: "/t/$tenantId/couriers", label: "nav.couriers", icon: Bike, any: ["order.manage"] },
  { to: "/t/$tenantId/products", label: "nav.products", icon: Package, any: ["item.view"] },
  { to: "/t/$tenantId/stock", label: "nav.stock", icon: Boxes, any: ["stock.view"] },
  { to: "/t/$tenantId/purchases", label: "nav.purchases", icon: Truck, any: ["purchase.view"] },
  { to: "/t/$tenantId/customers", label: "nav.customers", icon: Users, any: ["customer.view"] },
  {
    to: "/t/$tenantId/suppliers",
    label: "nav.suppliers",
    icon: WalletCards,
    any: ["purchase.view"],
  },
  {
    to: "/t/$tenantId/wholesale",
    label: "nav.wholesale",
    icon: Tags,
    any: ["item.manage", "customer.manage"],
  },
  {
    to: "/t/$tenantId/loyalty",
    label: "nav.loyalty",
    icon: Gift,
    any: ["tenant.settings.manage"],
  },
  {
    to: "/t/$tenantId/accounts",
    label: "nav.accounts",
    icon: Wallet,
    any: ["payment.manage", "cashbook.view"],
  },
  { to: "/t/$tenantId/expenses", label: "nav.expenses", icon: Receipt, any: ["expense.manage"] },
  {
    to: "/t/$tenantId/reports",
    label: "nav.reports",
    icon: BarChart3,
    any: ["report.sales.view", "report.stock.view", "report.finance.view"],
  },
  { to: "/t/$tenantId/vat", label: "nav.vat", icon: FileText, any: ["vat.manage"] },
  {
    to: "/t/$tenantId/settings",
    label: "nav.settings",
    icon: Settings,
    any: ["tenant.settings.manage"],
  },
  { to: "/t/$tenantId/team", label: "nav.team", icon: UsersRound, any: ["member.manage"] },
  {
    to: "/t/$tenantId/developers",
    label: "nav.developers",
    icon: Code2,
    any: ["developer.manage"],
  },
  {
    to: "/t/$tenantId/billing",
    label: "nav.billing",
    icon: CreditCard,
    any: ["tenant.billing.manage"],
  },
  { to: "/t/$tenantId/sessions", label: "nav.sessions", icon: MonitorSmartphone },
];

const linkClass =
  "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground data-[status=active]:bg-accent data-[status=active]:text-accent-foreground";

function Shell() {
  const { tenantId, settings, branches, branch, setBranchId, can, support } = useTenant();
  const { data: overviewData } = useSuspenseQuery(overviewQuery(tenantId));
  useLiveUpdates(tenantId);
  const { t, num } = useI18n();
  const navigate = useNavigate();
  const matchRoute = useMatchRoute();
  const queryClient = useQueryClient();
  const { data: me } = useSuspenseQuery(meQuery);
  const [moreOpen, setMoreOpen] = useState(false);
  const [limit, setLimit] = useState<BusinessAllowance | null>(null);
  const [checkingAllowance, setCheckingAllowance] = useState(false);
  // Check before sending them through onboarding, so nobody fills a form they can't submit.
  const addBusiness = async () => {
    setCheckingAllowance(true);
    try {
      const allowance = await queryClient.fetchQuery(allowanceQuery);
      if (allowance.canCreate) navigate({ to: "/onboarding" });
      else setLimit(allowance);
    } catch {
      navigate({ to: "/onboarding" }); // The server still enforces the limit on submit.
    } finally {
      setCheckingAllowance(false);
    }
  };

  const [blocked, setBlocked] = useState(false);
  const signOut = useMutation({
    mutationFn: async () => {
      // Signing out must not strand sales saved on this device (FR-OFF-05).
      const unsynced = await outboxFor(tenantId).catch(() => []);
      if (unsynced.length > 0) return "blocked" as const;
      await api.post("/auth/logout").catch(() => undefined);
      return "done" as const;
    },
    onSuccess: async (result) => {
      if (result === "blocked") {
        setBlocked(true);
        return;
      }
      queryClient.clear();
      await navigate({ to: "/login" });
    },
  });
  const leaveSupport = useMutation({
    mutationFn: () => api.post(`/t/${tenantId}/support-access/end`, {}),
    onSettled: async () => {
      queryClient.removeQueries({ queryKey: ["tenant", tenantId] });
      await navigate({ to: "/admin" });
    },
  });
  const saveLocale = useMutation({
    mutationFn: (locale: "bn" | "en") => api.patch("/auth/me", { locale }),
  });

  // New online orders waiting for a decision, shown beside Orders in the sidebar.
  const pendingOrders = useQuery({
    queryKey: ["tenant", tenantId, "orders", "pending-count", branch.id],
    queryFn: () =>
      api.get<{ counts: { pending: number } | null }>(
        `/t/${tenantId}/orders?status=pending&limit=1&branchId=${branch.id}`,
      ),
    enabled: can("order.manage"),
    refetchInterval: 60_000,
  });
  const pendingCount = pendingOrders.data?.counts?.pending ?? 0;

  const memberships = me?.memberships.filter((m) => m.tenantStatus === "active") ?? [];
  const items = NAV.filter((item) => !item.any || item.any.some((p) => can(p)));
  const mobileItems = items.filter((i) => i.mobile);
  const fullScreen = !!matchRoute({ to: "/t/$tenantId/pos" });

  // Platform notices and the support-visit bar show on every screen, the till included.
  const banners = (
    <>
      <PlatformBanners overview={overviewData} />
      {support && (
        <div
          className={`flex flex-wrap items-center justify-center gap-3 px-4 py-2 text-sm font-medium ${
            support.mode !== "read"
              ? "bg-amber-500 text-black"
              : "bg-primary text-primary-foreground"
          }`}
          role="status"
        >
          <LifeBuoy className="size-4" aria-hidden />
          {t(
            support.mode === "full"
              ? "supportVisit.bannerFull"
              : support.mode === "write"
                ? "supportVisit.bannerWrite"
                : "supportVisit.bannerRead",
            {
              time: new Date(support.expiresAt).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              }),
            },
          )}
          <Button
            size="sm"
            variant="outline"
            className="h-7 bg-background text-foreground"
            loading={leaveSupport.isPending}
            onClick={() => leaveSupport.mutate()}
          >
            {t("supportVisit.leave")}
          </Button>
        </div>
      )}
    </>
  );

  // The till takes the whole screen: no menu, header or footer under it.
  if (fullScreen)
    return (
      <div className="flex h-dvh flex-col bg-background">
        {banners}
        <Outlet />
        <ForcePasswordChange />
      </div>
    );

  return (
    <div className="flex min-h-dvh flex-col md:flex-row">
      <aside className="sticky top-0 hidden h-dvh w-60 shrink-0 flex-col border-r bg-card md:flex">
        <div className="flex h-14 items-center px-5">
          <Logo />
        </div>
        <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-2" aria-label="Main">
          {items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              params={{ tenantId }}
              activeOptions={{ exact: item.to === "/t/$tenantId" }}
              className={linkClass}
            >
              <item.icon className="size-4" aria-hidden />
              {t(item.label)}
              {item.to === "/t/$tenantId/orders" && pendingCount > 0 && (
                <span className="ml-auto rounded-full bg-primary px-1.5 text-xs text-primary-foreground tabular-nums">
                  {num(pendingCount)}
                </span>
              )}
            </Link>
          ))}
        </nav>
        <div className="border-t p-3">
          {me?.platformRole && (
            <Link to="/admin" className={linkClass}>
              <ShieldCheck className="size-4" aria-hidden />
              {t("admin.openAdmin")}
            </Link>
          )}
          <Button
            variant="ghost"
            className="w-full justify-start"
            loading={signOut.isPending}
            onClick={() => signOut.mutate()}
          >
            <LogOut aria-hidden />
            {t("common.signOut")}
          </Button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-10 flex h-14 items-center gap-2 border-b bg-background/90 px-4 backdrop-blur sm:px-6">
          <div className="md:hidden">
            <Logo className="[&>svg]:size-6 [&]:text-[0px] sm:[&]:text-base" />
          </div>
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {memberships.length > 1 ? (
              <Select
                aria-label={t("common.switchBusiness")}
                className="h-9 max-w-56"
                value={tenantId}
                onChange={(e) =>
                  navigate({ to: "/t/$tenantId", params: { tenantId: e.target.value } })
                }
              >
                {memberships.map((m) => (
                  <option key={m.tenantId} value={m.tenantId}>
                    {m.tenantName}
                  </option>
                ))}
              </Select>
            ) : (
              <p className="truncate text-sm font-semibold">{settings.businessName}</p>
            )}
            {branches.length > 1 && (
              <Select
                aria-label={t("ui.branch")}
                className="h-9 max-w-44"
                value={branch.id}
                onChange={(e) => setBranchId(e.target.value)}
              >
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t("common.addBusiness")}
            disabled={checkingAllowance}
            onClick={addBusiness}
          >
            <Plus aria-hidden />
          </Button>
          <AskZeetaButton />
          <ThemeToggle />
          <LanguageToggle onChange={(l) => saveLocale.mutate(l)} />
        </header>
        {banners}
        <main className="flex-1 px-4 py-6 sm:px-6">
          {me && !me.user.email && (
            <div className="mx-auto mb-4 flex max-w-5xl flex-wrap items-center justify-between gap-2 rounded-lg border bg-card p-3 text-sm">
              {t("account.banner")}
              <Link
                to="/t/$tenantId/sessions"
                params={{ tenantId }}
                className="font-medium text-primary hover:underline"
              >
                {t("account.bannerAction")}
              </Link>
            </div>
          )}
          <Outlet />
          <ForcePasswordChange />
        </main>
        <SiteFooter className="mb-16 md:mb-0" />
        <nav
          className="fixed inset-x-0 bottom-0 z-10 flex border-t bg-card pb-[env(safe-area-inset-bottom)] md:hidden"
          aria-label="Main"
        >
          {mobileItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              params={{ tenantId }}
              activeOptions={{ exact: item.to === "/t/$tenantId" }}
              className="flex flex-1 flex-col items-center gap-1 py-2 text-xs text-muted-foreground data-[status=active]:text-primary"
            >
              <item.icon className="size-5" aria-hidden />
              {t(item.label)}
            </Link>
          ))}
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            className="flex flex-1 flex-col items-center gap-1 py-2 text-xs text-muted-foreground"
          >
            <Menu className="size-5" aria-hidden />
            {t("nav.more")}
          </button>
        </nav>
        <Dialog
          open={blocked}
          onClose={() => setBlocked(false)}
          title={t("common.signOut")}
          size="sm"
        >
          <p className="text-sm">{t("offline.signOutBlocked")}</p>
        </Dialog>
        <Dialog
          open={!!limit}
          onClose={() => setLimit(null)}
          title={t("businessLimit.title")}
          size="sm"
        >
          {limit && <BusinessLimitNotice allowance={limit} onNavigate={() => setLimit(null)} />}
        </Dialog>
        <Dialog open={moreOpen} onClose={() => setMoreOpen(false)} title={t("nav.more")} size="sm">
          <nav className="space-y-0.5" aria-label="More">
            {items
              .filter((i) => !i.mobile)
              .map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  params={{ tenantId }}
                  className={linkClass}
                  onClick={() => setMoreOpen(false)}
                >
                  <item.icon className="size-4" aria-hidden />
                  {t(item.label)}
                </Link>
              ))}
            {me?.platformRole && (
              <Link to="/admin" className={linkClass} onClick={() => setMoreOpen(false)}>
                <ShieldCheck className="size-4" aria-hidden />
                {t("admin.openAdmin")}
              </Link>
            )}
            <button
              type="button"
              className={`${linkClass} w-full`}
              onClick={() => signOut.mutate()}
            >
              <LogOut className="size-4" aria-hidden />
              {t("common.signOut")}
            </button>
          </nav>
        </Dialog>
      </div>
    </div>
  );
}

export function AppShell() {
  const { tenantId } = useParams({ from: "/t/$tenantId" });
  const { data: overview } = useSuspenseQuery(overviewQuery(tenantId));
  return (
    <TenantProvider key={tenantId} overview={overview}>
      <Shell />
    </TenantProvider>
  );
}
